/**
 * THE LADDER (docs/01 §5.2) — Elo, K = 32, from 1200.
 *
 * The standings are the boring half. The stats that make a leaderboard worth
 * looking at are all derived from the match log at no storage cost — head-to-
 * head ("you are 12-4 against Omar"), current and best win streaks, and the
 * biggest upset by rating gap — so they are on this screen, not hidden behind a
 * "stats" tab.
 *
 * Recording a match is two steps, exactly as specified: pick the sides, enter
 * the score. Ratings shown here already include a match still sitting in the
 * outbox, so the ladder moves the moment you tap save rather than whenever the
 * network comes back.
 */

import { useState } from 'react'
import {
  SpaceEmpty,
  SpaceStyles,
  effectiveRatings,
  formatAgo,
  formatDayMonth,
  mutationCtx,
  nameLookup,
  useAction,
  useNow,
  useSpace,
  type SpacePerson,
  type SpaceScreenProps,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'
import {
  biggestUpsetFromLog,
  displayRating,
  headToHead,
  leaderboard,
  matchParticipants,
  sideOf,
  streakSummary,
} from '@office/domain/resources'
import type { Match, PersonId, Resource } from '@office/domain'

export interface LadderProps extends SpaceScreenProps {
  readonly tickMs?: number
}

export function Ladder(props: LadderProps) {
  const { db, clock, actorId } = props
  const now = useNow(clock, props.tickMs ?? 60_000)
  const space = useSpace(db)
  const run = useAction()
  const nameOf = nameLookup(space.people)

  const competitive = space.resources.filter(r => r.competitive && r.active)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const resource: Resource | null =
    competitive.find(r => String(r.id) === selectedId) ?? competitive[0] ?? null

  if (resource === null) {
    return (
      <section className="sp" aria-labelledby="sp-ladder-h">
        <SpaceStyles />
        <h2 className="sp-title" id="sp-ladder-h">
          Ladder
        </h2>
        <SpaceEmpty mark="🏆" hint="Any contended resource can carry a ladder: the ping pong table now, FIFA on the PlayStation later.">
          No competitive resources yet.
        </SpaceEmpty>
      </section>
    )
  }

  const matches = space.matches.filter(m => String(m.resourceId) === String(resource.id))
  const ratings = effectiveRatings(space.ratings, space.matches, space.pending).filter(
    r => String(r.resourceId) === String(resource.id),
  )
  const standings = leaderboard(ratings, resource.id)
  const mine = standings.find(r => String(r.personId) === actorId) ?? null
  const summary = streakSummary(matches, actorId as PersonId, resource.id)
  const upset = biggestUpsetFromLog(matches, undefined, resource.id)

  const opponents = opponentTotals(matches, actorId)
  const lastPlayedAt = matches.reduce<number | null>(
    (latest, m) => (latest === null || m.playedAt > latest ? m.playedAt : latest),
    null,
  )

  return (
    <section className="sp" aria-labelledby="sp-ladder-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-ladder-h">
          Ladder
        </h2>
        <span className="sp-note">Elo · K 32 · from 1200</span>
      </div>

      {competitive.length > 1 ? (
        <div className="sp-seg" role="group" aria-label="Choose a ladder">
          {competitive.map(r => (
            <button
              key={r.id}
              type="button"
              aria-pressed={String(r.id) === String(resource.id)}
              onClick={() => setSelectedId(String(r.id))}
            >
              {r.name}
            </button>
          ))}
        </div>
      ) : null}

      <div className="card stack gap-2">
        <span className="sp-note">Your rating · {resource.name}</span>
        <span className="sp-figure">{mine === null ? '1200' : displayRating(mine)}</span>
        <span className="sp-note">
          {summary.wins}W – {summary.losses}L
          {mine?.provisional === true ? ' · provisional' : ''}
        </span>
        <span className="sp-note">
          {describeStreak(summary.current)} · best {summary.longestWin}
        </span>
      </div>

      {recording ? (
        <RecordMatch
          resource={resource}
          people={space.people}
          actorId={actorId}
          onCancel={() => setRecording(false)}
          onSave={entry => {
            run(() =>
              mutations.recordMatch(mutationCtx(props), {
                resourceId: String(resource.id),
                kind: entry.kind,
                sideA: entry.sideA,
                sideB: entry.sideB,
                scoreA: entry.scoreA,
                scoreB: entry.scoreB,
              }),
            )
            setRecording(false)
          }}
        />
      ) : (
        <button type="button" className="btn btn-primary" onClick={() => setRecording(true)}>
          Record a match
        </button>
      )}

      <div className="card">
        <h3 className="sp-card-title">
          Standings
        </h3>
        {standings.length === 0 ? (
          <SpaceEmpty mark="🏅" hint="Everyone starts at 1200 and stays provisional until they have played enough for the number to mean something.">
            No matches played yet.
          </SpaceEmpty>
        ) : (
          <ol className="sp-list sp-rows" aria-label="Standings">
            {standings.map((r, i) => (
              <li key={String(r.personId)} className="sp-row">
                <span className="sp-rank">{i + 1}</span>
                <span className="grow">
                  {nameOf(String(r.personId))}
                  {String(r.personId) === actorId ? ' (you)' : ''}
                  {r.provisional ? <span className="pill sp-provisional">new</span> : null}
                </span>
                <span className="sp-note">
                  {r.wins}–{r.losses}
                </span>
                <strong className="sp-rating">
                  {displayRating(r)}
                </strong>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Head to head
        </h3>
        {opponents.length === 0 ? (
          <SpaceEmpty mark="⚔" hint="Record a match and the head-to-head record, the streaks and the biggest upset all follow from it.">
            You have not played anyone here yet.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Head to head records">
            {opponents.map(opponentId => {
              const h2h = headToHead(matches, actorId as PersonId, opponentId, resource.id)
              return (
                <li key={String(opponentId)} className="sp-row">
                  <span className="grow">
                    You are {h2h.aWins}–{h2h.bWins} against {nameOf(String(opponentId))}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {upset !== null ? (
        <div className="card stack gap-1">
          <h3 className="sp-card-title">
            Biggest upset
          </h3>
          <p>
            {describeSide(upset.match, upset.match.winner, nameOf)} beat{' '}
            {describeSide(upset.match, upset.match.winner === 'a' ? 'b' : 'a', nameOf)}
          </p>
          <p className="sp-note">
            {Math.round(upset.gap)} rating points against them · {formatDayMonth(upset.match.playedAt)}
          </p>
        </div>
      ) : null}

      <p className="sp-note" data-testid="ladder-last-match">
        {lastPlayedAt === null
          ? 'No matches recorded yet.'
          : `Last match ${formatAgo(lastPlayedAt, now)}.`}
      </p>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Record a match — step 1 sides, step 2 score
// ---------------------------------------------------------------------------

export interface MatchEntry {
  readonly kind: 'singles' | 'doubles'
  readonly sideA: string[]
  readonly sideB: string[]
  readonly scoreA: number
  readonly scoreB: number
}

export interface RecordMatchProps {
  readonly resource: Resource
  readonly people: readonly SpacePerson[]
  readonly actorId: string
  readonly onSave: (entry: MatchEntry) => void
  readonly onCancel: () => void
}

/**
 * You are always on side A. The phone records *your* matches; the tablet, which
 * anyone walking past can use, is where a match between two other people gets
 * entered. That keeps this to two steps instead of four.
 */
export function RecordMatch({ resource, people, actorId, onSave, onCancel }: RecordMatchProps) {
  const [kind, setKind] = useState<'singles' | 'doubles'>('singles')
  const [partner, setPartner] = useState<string | null>(null)
  const [opponents, setOpponents] = useState<string[]>([])
  const [step, setStep] = useState<'sides' | 'score'>('sides')
  const [scoreA, setScoreA] = useState(0)
  const [scoreB, setScoreB] = useState(0)

  const others = people.filter(p => String(p.id) !== actorId)
  const needOpponents = kind === 'singles' ? 1 : 2
  const sidesReady =
    opponents.length === needOpponents && (kind === 'singles' || partner !== null)
  const scoreValid = scoreA !== scoreB

  const toggleOpponent = (id: string) => {
    setOpponents(current => {
      if (current.includes(id)) return current.filter(x => x !== id)
      if (current.length >= needOpponents) return [...current.slice(1), id]
      return [...current, id]
    })
  }

  const switchKind = (next: 'singles' | 'doubles') => {
    setKind(next)
    setOpponents([])
    setPartner(null)
  }

  const sideA = kind === 'doubles' && partner !== null ? [actorId, partner] : [actorId]

  return (
    <form
      className="card stack gap-3"
      aria-label="Record a match"
      onSubmit={e => {
        e.preventDefault()
        if (step === 'sides') {
          if (sidesReady) setStep('score')
          return
        }
        if (scoreValid) onSave({ kind, sideA, sideB: opponents, scoreA, scoreB })
      }}
    >
      <div className="sp-head">
        <h3 className="sp-card-title">
          {step === 'sides' ? 'Pick the sides' : 'Enter the score'}
        </h3>
        <span className="sp-note">{resource.name}</span>
      </div>

      {step === 'sides' ? (
        <>
          <div className="sp-seg" role="group" aria-label="Match kind">
            <button type="button" aria-pressed={kind === 'singles'} onClick={() => switchKind('singles')}>
              Singles
            </button>
            <button type="button" aria-pressed={kind === 'doubles'} onClick={() => switchKind('doubles')}>
              Doubles
            </button>
          </div>

          {kind === 'doubles' ? (
            <fieldset className="sp-fieldset-bare">
              <legend className="sp-note">Your partner</legend>
              <div className="sp-chips">
                {others
                  .filter(p => !opponents.includes(String(p.id)))
                  .map(p => (
                    <button
                      key={p.id}
                      type="button"
                      className="sp-chip"
                      aria-pressed={partner === String(p.id)}
                      onClick={() => setPartner(partner === String(p.id) ? null : String(p.id))}
                    >
                      {p.displayName}
                    </button>
                  ))}
              </div>
            </fieldset>
          ) : null}

          <fieldset className="sp-fieldset-bare">
            <legend className="sp-note">
              {kind === 'singles' ? 'Opponent' : 'Opponents (2)'}
            </legend>
            <div className="sp-chips">
              {others
                .filter(p => partner !== String(p.id))
                .map(p => (
                  <button
                    key={p.id}
                    type="button"
                    className="sp-chip"
                    aria-pressed={opponents.includes(String(p.id))}
                    onClick={() => toggleOpponent(String(p.id))}
                  >
                    {p.displayName}
                  </button>
                ))}
            </div>
          </fieldset>

          <div className="row gap-2">
            <button type="submit" className="btn btn-primary grow" disabled={!sidesReady}>
              Next
            </button>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <ScoreRow label="You" value={scoreA} onChange={setScoreA} />
          <ScoreRow label="Them" value={scoreB} onChange={setScoreB} />
          {!scoreValid ? (
            <p className="sp-note" role="status">
              A match needs a winner — the scores cannot be level.
            </p>
          ) : (
            <p className="sp-note" role="status">
              {scoreA > scoreB ? 'You win' : 'They win'} {Math.max(scoreA, scoreB)}–
              {Math.min(scoreA, scoreB)}
            </p>
          )}
          <div className="row gap-2">
            <button type="submit" className="btn btn-primary grow" disabled={!scoreValid}>
              Save match
            </button>
            <button type="button" className="btn" onClick={() => setStep('sides')}>
              Back
            </button>
          </div>
        </>
      )}
    </form>
  )
}

function ScoreRow({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (n: number) => void
}) {
  const id = `sp-score-${label.toLowerCase()}`
  return (
    <div className="row gap-2">
      <label className="grow" htmlFor={id}>
        {label}
      </label>
      <button
        type="button"
        className="sp-stepper"
        aria-label={`Decrease ${label} score`}
        onClick={() => onChange(Math.max(0, value - 1))}
      >
        −
      </button>
      <input
        id={id}
        className="sp-score-input"
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={e => {
          const next = Number(e.target.value)
          onChange(Number.isFinite(next) ? Math.max(0, Math.floor(next)) : 0)
        }}
      />
      <button
        type="button"
        className="sp-stepper"
        aria-label={`Increase ${label} score`}
        onClick={() => onChange(value + 1)}
      >
        +
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------

function opponentTotals(matches: readonly Match[], actorId: string): PersonId[] {
  const counts = new Map<string, number>()
  for (const m of matches) {
    const mine = sideOf(m, actorId as PersonId)
    if (mine === null) continue
    for (const p of matchParticipants(m)) {
      if (sideOf(m, p) === mine) continue
      counts.set(String(p), (counts.get(String(p)) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .map(([id]) => id as PersonId)
}

export function describeStreak(current: number): string {
  if (current > 0) return `${current} win${current === 1 ? '' : 's'} in a row`
  if (current < 0) return `${-current} loss${current === -1 ? '' : 'es'} in a row`
  return 'No streak yet'
}

function describeSide(match: Match, side: 'a' | 'b', nameOf: (id: string) => string): string {
  const ids = side === 'a' ? match.sideA : match.sideB
  return ids.map(id => nameOf(String(id))).join(' & ') || 'Someone'
}
